import { FC, Fragment } from 'react';

import { InvisibleHoverArea } from '@eneco/custom-components/Graph';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { theme } from '@eneco/sparky/theme';
import { Group } from '@visx/group';
import { BarRounded } from '@visx/shape';
import type { ScaleBand, ScaleLinear } from 'd3-scale';


import { RatingIndicator } from './RatingIndicator';
import { TooltipData } from './Tooltip/Tooltip';
import { useDynamicPricingTariffsOptional } from '../../../hooks/useDynamicPricingTariffs';
import { graphColors } from '../../../utils/constants';
import { getBarWidth } from '../../../utils/helpers';

type Props = {
  yScale: ScaleLinear<number, number, never>;
  xScale: ScaleBand<Date>;
  onHover: (data: TooltipData) => void;
  height: number;
  width: number;
  threshold?: number;
};

export const DataCanvas: FC<Props> = ({ xScale, yScale, height, width, onHover, threshold }) => {
  const context = useDynamicPricingTariffsOptional();
  const isDesktop = useMediaQuery('md');
  if (!context) return null;
  const { data, averagePrice, selectedView, selectedProduct, showTotal } = context;
  if (!data || !selectedProduct) return null;

  return (
    <Group>
      {/* Line for mock peak price limit */}
      {threshold ? (
        <line
          x1={0}
          x2={width}
          y1={yScale(threshold)}
          y2={yScale(threshold)}
          stroke={theme.colors.brandOrange}
        />
      ) : null}

      <Group>
        {averagePrice && (
          <line
            x1={0}
            x2={width}
            y1={yScale(averagePrice)}
            y2={yScale(averagePrice)}
            stroke={graphColors?.averagePrice}
            strokeWidth={1.5}
            strokeOpacity={1}
            strokeDasharray={'4,4'}
            strokeLinejoin="round"
          />
        )}
        {data.map((item, index) => {
          const tariff = showTotal ? item.total : item.wholesale;
          const isMissingData = Number.isNaN(tariff);
          const yScaleAtZero = yScale(0);
          const isBarBelowZero = tariff < 0;
          const yScaleValue = isBarBelowZero ? yScale(Math.min(-0.01, tariff)) : yScale(Math.max(0.01, tariff));
          const barHeight = isBarBelowZero ? yScaleValue - yScaleAtZero : yScaleAtZero - yScaleValue;
          const barWidth = getBarWidth(selectedView, isDesktop);
          const hoverAreaWidth = barWidth * 2;
          const xValue = xScale(item.start) || 5;
          const xValueBar = xValue - barWidth / 2;
          const getYValue = () => {
            if (isMissingData) return yScaleAtZero - 4;

            return isBarBelowZero ? yScaleAtZero : yScaleValue;
          };

          const getFillColor = () => {
            if (isMissingData) return graphColors?.unknownPrice;
            if (selectedProduct === 'gas') return graphColors?.[`${selectedProduct}Product`];

            const tariffKey = item.rating === 'expensive' ? 'expensive' : 'cheap';

            return graphColors?.[`${tariffKey}Tariff`];
          };

          const yValue = getYValue();
          const toolTipValues = {
            x: xValue,
            y: yValue,
            ...item,
          };
          return (
            <Fragment key={`bar-${index}-${item.start.getTime()}`}>
              <BarRounded
                top={!isBarBelowZero}
                bottom={isBarBelowZero}
                radius={6}
                x={xValueBar}
                y={yValue}
                width={barWidth}
                height={isMissingData ? 4 : barHeight}
                fill={getFillColor()}
              />
              <InvisibleHoverArea
                points={[
                  [xValue + hoverAreaWidth / 2, height],
                  [xValue + hoverAreaWidth / 2, 0],
                  [xValue - hoverAreaWidth / 2, 0],
                  [xValue - hoverAreaWidth / 2, height],
                ]}
                width={barWidth}
                onMouseOver={() => onHover(toolTipValues)}
              />
              <RatingIndicator left={xValue} top={height + 42} rating={item.rating} />
            </Fragment>
          );
        })}
      </Group>
    </Group>
  );
};
